import React, { Fragment, useState, useEffect, useCallback, memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {  Grid, TextField, CircularProgress, Avatar, IconButton, Fab, Dialog,
	  DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
	  List, ListItem, ListSubheader, Divider } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Delete, Add, Refresh } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import firebase from '../Firebase.js';
import { DialogAcceptButton, DialogCancelButton, isEmailValid } from '../Common.js';
import HeaderBar from './HeaderBar.js';
import MainNavigation from './MainNavigation.js';
import { TrainerContext } from '../contexts/TrainerContext.js';

const Loader = cl => <Grid container justify='center' alignItems='center' className={cl}>
		       <CircularProgress color='secondary'/>
		     </Grid>;

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      container: {
	overflow: 'auto',
	'-webkit-overflow-scrolling': 'touch',
	marginTop: theme.spacing(2),
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      searchBox: {
	backgroundColor: theme.palette.background.paper,
      },
      avatar: {
	width: 70,
	height: 70,
      },
      removeDialogContent: {
	padding: 0,
      },
      hasRegistered: {
	color: theme.palette.primary.green,
      },
      hasNotRegistered: {
	color: theme.palette.primary.red,
      },
      addClientBtn: {
	position: 'absolute',
	bottom: theme.spacing(2),
	right: theme.spacing(2),
	color: theme.palette.primary.main,
	background: theme.palette.primary.green,
	'&:hover': {
	  background: theme.palette.primary.green,
	},
      },
    }),
);

const AddClient = memo(({ addClient }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const close = _ => setIsOpen(false);
  const open = _ => setIsOpen(true);

  const createClientForTrainer = async () => {
    setCreating(true);
    try {
      const client = await firebase.functions()
	    .httpsCallable('createClientForTrainer')({email});
      addClient({ ...client.data, email });
    }
    catch (err) {
      console.log('error creating client', err.message);
    }
    finally {
      setCreating(false);
      close();
    }
  };

  return (
    <Fragment>
      <Fab aria-label={t('add-client')}
	   onClick={open}
	   className={classes.addClientBtn}>
	<Add />
      </Fab>
      <Dialog open={isOpen}
	      onClose={() => {
		if (creating) {
		  return;
		}
		close();
	      }}>
	<DialogTitle>
	  {t('add-new-client')}
	</DialogTitle>
	<DialogContent>
	  <DialogContentText>
	    {t('info-adding-new-client')}
	  </DialogContentText>
	  <TextField
	    margin="dense"
	    label={t('email')}
	    type="email"
	    onChange={(e) => setEmail(e.target.value.trim())}
	    fullWidth
	  />
	</DialogContent>
	<DialogActions>
	  <Button onClick={close}
		  disabled={creating}
		  color="secondary">
	    {t('cancel')}
	  </Button>
	  <Button onClick={createClientForTrainer}
		  disabled={!isEmailValid(email) || creating}
		  color="secondary">
	    { creating ? (<CircularProgress size={20} color='secondary' />) : t('save') }
	  </Button>
	</DialogActions>
      </Dialog>
    </Fragment>
  );
});


const DeleteClientDialog = memo(({ client, deleteClient }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = useCallback(_ => {
    setOpen(false);
  }, []);
  const [deleting, setDeleting] = useState(false);
  const deleteClientFb = async () => {
    setDeleting(true);
    try {
      await firebase.firestore()
	.collection('trainers').doc(firebase.auth().currentUser.email)
	.collection('clients').doc(client.email).delete();
      setDeleting(false);
      close();
      deleteClient(client);
    }
    catch (err) {
      setDeleting(false);
      close();
      console.log('error deleting a client');
    }
  };

  return (
    <Fragment>
      <IconButton aria-label='delete'
		  size='medium'
		  onClick={() => setOpen(true)}>
	<Delete fontSize='large' color='error' />
      </IconButton>

      <Dialog open={open}
	      onClose={close}
	      fullWidth>
	<DialogTitle style={{textAlign: 'center'}}
		     disableTypography
		     onClose={() => {}}>
	  <Typography gutterBottom variant='h6'>
	    { t('Delete client') + ' '  + client.email + '?' }
	  </Typography>
	  <Typography variant='body2'>
	    { t('are-you-sure-delete') }
	  </Typography>
	</DialogTitle>
	<DialogContent dividers className={classes.removeDialogContent}>
	  <DialogAcceptButton fullWidth
			      disabled={deleting}
			      onClick={deleteClientFb}>
	    {
	      deleting ? (<CircularProgress size={20} color='secondary' />) : t('delete')
	    }
	  </DialogAcceptButton>
	  <Divider/>
	  <DialogCancelButton fullWidth
			      disabled={deleting}
			      onClick={close}>
	    { t('cancel') }
	  </DialogCancelButton>
	</DialogContent>
      </Dialog>
    </Fragment>
  );
});

const CheckIfRegistered = ({ client, updateClient }) => {
  const [checking, setChecking] = useState(false);

  const checkIfClientIsRegistered = async () => {
    setChecking(true);
    try {
      const isRegistered = await firebase.functions()
	    .httpsCallable('checkIfClientIsRegistered')({email: client.email});
      if (isRegistered.data) {
	const { name, avatar } = isRegistered.data;
	await firebase.firestore()
	  .collection('trainers').doc(firebase.auth().currentUser.email)
	  .collection('clients').doc(client.email)
	  .set({name, avatar, registeredInAuth: true}, {merge: true});
	setChecking(false);
	updateClient(client, {name, avatar, registeredInAuth: true});
	return;
      }
      setChecking(false);
    }
    catch(err) {
      console.log('error checking if registered');
      setChecking(false);
    }
  };

  return (
    checking ?
      <Grid container justify='center'>
	<CircularProgress size={25} color='secondary' />
      </Grid> :
    <IconButton aria-label='refresh'
		size='medium'
		onClick={checkIfClientIsRegistered}>
      <Refresh fontSize='large' color='secondary' />
    </IconButton>
  );
};

const Client = memo(({ client, isLastItem, updateClient, deleteClient }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Fragment>
      <ListItem>
	<Grid container alignItems='center'>
	  <Grid item xs={3}>
	    <Avatar alt='avatar' src={client.avatar} className={classes.avatar}/>
	  </Grid>
	  <Grid item container xs={5} direction='column'>
	    <Typography variant='body1'>
	      {client.name}
	    </Typography>
	    <Typography gutterBottom variant='body1'>
	      {client.email}
	    </Typography>
	    <Typography gutterBottom variant='subtitle2'>
	      <span className={client.registeredInAuth ? classes.hasRegistered : classes.hasNotRegistered}>
		{ client.registeredInAuth ? t('client-is-in-system-already') : t('client-has-not-register-dogo-yet') }
	      </span>
	    </Typography>
	  </Grid>
	  <Grid item xs={2}>
	    { !client.registeredInAuth && <CheckIfRegistered client={client}
							     updateClient={updateClient}/> }
	  </Grid>
	  <Grid item xs={2}>
	    <DeleteClientDialog client={client}
				deleteClient={deleteClient}/>
	  </Grid>
	</Grid>
      </ListItem>
      <Divider style={isLastItem ? {marginBottom: '80px'} : {}}/>
    </Fragment>
  );
});

const ClientsList = ({ clients, updateClient, deleteClient }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  const filterClients = _ => {
    const txt = searchText.toLowerCase();
    return clients.filter((item) => {
      return item.email.toLowerCase().includes(txt);
    });
  };

  return (
    <Fragment>
      <List disablePadding className={classes.container}>
	<ListSubheader className={classes.searchBox}>
	  <Grid container>
	    <TextField
	      fullWidth
	      type='search'
	      value={searchText}
	      placeholder={t('search-client')}
	      onChange={(e) => setSearchText(e.target.value.trim())}
	      margin='normal'/>
	  </Grid>
	</ListSubheader>
	{ filterClients().map((client, i) => <Client key={client.email}
						     client={client}
						     isLastItem={filterClients().length - 1 === i}
						     updateClient={updateClient}
						     deleteClient={deleteClient} />)}
      </List>
    </Fragment>
  );
};

export default () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { clients, setState } = useContext(TrainerContext);

  const addClient = useCallback((client) => {
    setState(s => {
      return { ...s, clients: [client].concat(s.clients) };
    });
  }, []);

  const updateClient = useCallback((client, updatedFields) => {
    setState(s => {
      return { ...s, clients: s.clients.map(c => c.email !== client.email ? c : {...c, ...updatedFields})};
    });
  }, []);

  const deleteClient = useCallback((client) => {
    setState(s => {
      return { ...s, clients: s.clients.filter((c) => c.email !== client.email)};
    });
  }, []);

  useEffect(() => {
    const getMyClients = async () => {
      try {
	const rez = await firebase.firestore()
	      .collection('trainers').doc(firebase.auth().currentUser.email)
	      .collection('clients')
	      .orderBy('createdAt', 'desc')
	      .get();
	const clients = rez.docs.map((doc) => {
	  return { ...doc.data(), email: doc.id };
	});
	setState(s => {
	  return { ...s, clients};
	});
      }
      catch (errors) {
	console.log(errors);
	setState(s => {
	  return { ...s, clients: [] };
	});
      }
    };
    if (clients === null) {
      getMyClients();
    }
  }, []);

  return (
    <div style={{ height: '100%' }}>
      <HeaderBar LComp={<MainNavigation />}
		 MComp={<Typography variant='h6' noWrap align='center'>{t('clients')}</Typography>}
		 RComp={<div></div>}/>
      { clients === null
	? Loader(classes.container)
	: <Fragment>
	    {
	      (() => {
		if (clients.length === 0) {
		  return (
		    <Grid className={classes.container} container justify='center' alignItems='center'>
		      <Typography variant='h2'>
			{t('no-clients-yet')}
		      </Typography>
		    </Grid>
		  );
		}
		return (
		  <ClientsList clients={clients}
			       addClient={addClient}
			       updateClient={updateClient}
			       deleteClient={deleteClient} />
		);
	      })()
	    }
	    <AddClient addClient={addClient}/>
	  </Fragment>}
    </div>
  );
};
